import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Bloque de código con barra estilo IDE.
 * lang: blox | c | gdscript | powershell | json | text
 */
export const CodeBox = ({ children, lang = 'blox', filename }) => (
  <div className="my-4 rounded-xl border border-cyan-900/40 overflow-hidden bg-slate-950">
    {filename && (
      <div className="flex items-center gap-2 px-4 py-2 bg-cyan-950/50 border-b border-cyan-900/40">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
        </div>
        <span className="text-xs text-cyan-500 ml-2 font-mono">{filename}</span>
        <span className="ml-auto text-[10px] text-slate-500 uppercase tracking-wider">{lang}</span>
      </div>
    )}
    <pre className="p-4 overflow-x-auto text-slate-300 font-mono text-sm leading-relaxed !bg-transparent">
      <code>{children}</code>
    </pre>
  </div>
);

/** Card con icono para features / bloques descriptivos */
export const FeatureCard = ({ icon: Icon, title, children }) => (
  <div className="p-6 rounded-2xl bg-slate-800/40 border border-slate-700/60 hover:border-cyan-500/40 transition-colors">
    <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-cyan-400" />
    </div>
    <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
    <div className="text-slate-400 text-sm leading-relaxed">{children}</div>
  </div>
);

/** Grupo de chips para listar hosts / entornos */
export const ChipGroup = ({ items }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((it) => (
      <span
        key={it}
        className="px-3 py-1.5 rounded-full bg-slate-800/60 border border-slate-700/70 text-slate-300 text-xs font-mono hover:border-cyan-500/40 hover:text-cyan-300 transition-colors"
      >
        {it}
      </span>
    ))}
  </div>
);

/** Enlace "volver a BLOX ESL" para las sub-páginas de demo */
export const EslBackLink = () => (
  <Link
    to="/embedded-scripting-language"
    data-testid="esl-back-link"
    className="inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors mb-4"
  >
    <ArrowLeft className="w-4 h-4" />
    Volver a BLOX ESL
  </Link>
);

/** Hero compacto para las páginas de demo */
export const DemoHero = ({ eyebrow, title, subtitle, accent = 'cyan' }) => {
  const accentColors = {
    cyan: 'text-cyan-400 border-cyan-500/30 bg-cyan-500/10',
    amber: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
    fuchsia: 'text-fuchsia-400 border-fuchsia-500/30 bg-fuchsia-500/10',
    emerald: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
  };
  return (
    <div className="pt-32 sm:pt-28 pb-10">
      <EslBackLink />
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${accentColors[accent]} text-xs font-medium mb-4`}>
        {eyebrow}
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
        {title}
      </h1>
      <p className="text-slate-400 text-base sm:text-lg max-w-3xl leading-relaxed">
        {subtitle}
      </p>
    </div>
  );
};

/** Bloque destacado tipo callout / resultado esperado */
export const Callout = ({ children, tone = 'cyan', label }) => {
  const tones = {
    cyan: 'from-cyan-500/10 to-cyan-500/5 border-cyan-500/30',
    emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/30',
    amber: 'from-amber-500/10 to-amber-500/5 border-amber-500/30',
  };
  return (
    <div className={`my-6 p-5 rounded-xl bg-gradient-to-br ${tones[tone]} border`}>
      {label && (
        <div className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-2">
          {label}
        </div>
      )}
      <div className="text-slate-200 text-sm leading-relaxed">{children}</div>
    </div>
  );
};

/** Helper para setear document.title + meta description con cleanup */
export const useDocMeta = (title, description) => {
  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    const previousDesc = tag ? tag.getAttribute('content') : null;
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'description');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', description);
    return () => {
      document.title = previousTitle;
      if (tag && previousDesc !== null) tag.setAttribute('content', previousDesc);
    };
  }, [title, description]);
};
